import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Mail, FileText, Send, Loader2 } from 'lucide-react';

export function EmailPromptDialog({
  open, onOpenChange,
  dispatch,
  onYes, onNo,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-blue-600" />
            Email Dispatch Note?
          </DialogTitle>
          <DialogDescription>
            {dispatch?.dispatch_number 
              ? `Dispatch ${dispatch.dispatch_number} for ${dispatch.customer_name || 'customer'} has been marked as dispatched.`
              : 'Order has been dispatched.'
            }
          </DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground py-2">
          Would you like to email the dispatch note to the customer?
        </p>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onNo} data-testid="btn-email-prompt-no">
            No, Skip
          </Button>
          <Button onClick={onYes} data-testid="btn-email-prompt-yes">
            <Mail className="w-4 h-4 mr-2" /> Yes, Send Email
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function EmailComposeDialog({
  open, onOpenChange,
  dispatch,
  emailData, setEmailData,
  ccInput, setCcInput,
  onAddCc, onRemoveCc,
  onSend, sending,
  onCancel,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Send Dispatch Note via Email
          </DialogTitle>
          <DialogDescription>
            Send dispatch note #{dispatch?.dispatch_number} to the customer with PDF attachment.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>To *</Label>
            <Input
              type="email"
              value={emailData.to_email}
              onChange={(e) => setEmailData(prev => ({ ...prev, to_email: e.target.value }))}
              placeholder="customer@example.com"
              data-testid="input-dispatch-email-to"
            />
          </div>
          
          <div className="space-y-2">
            <Label>CC (optional)</Label>
            <div className="flex gap-2">
              <Input
                type="email"
                value={ccInput}
                onChange={(e) => setCcInput(e.target.value)}
                placeholder="Add CC email..."
                onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), onAddCc())}
              />
              <Button type="button" variant="outline" onClick={onAddCc}>Add</Button>
            </div>
            {emailData.cc_emails.length > 0 && (
              <div className="flex flex-wrap gap-1 mt-2">
                {emailData.cc_emails.map((email, idx) => (
                  <Badge key={idx} variant="secondary" className="text-xs">
                    {email}
                    <button type="button" onClick={() => onRemoveCc(email)} className="ml-1 hover:text-red-500">×</button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <Label>Subject *</Label>
            <Input
              value={emailData.subject}
              onChange={(e) => setEmailData(prev => ({ ...prev, subject: e.target.value }))}
              placeholder="Dispatch Note..."
              data-testid="input-dispatch-email-subject"
            />
          </div>
          
          <div className="space-y-2">
            <Label>Message</Label>
            <Textarea
              value={emailData.body}
              onChange={(e) => setEmailData(prev => ({ ...prev, body: e.target.value }))}
              placeholder="Enter your message..."
              className="h-32"
            />
          </div>
          
          <div className="flex items-center gap-2 p-3 bg-muted/50 rounded-lg">
            <Checkbox
              id="attach-pdf-planner"
              checked={emailData.attach_pdf}
              onCheckedChange={(v) => setEmailData(prev => ({ ...prev, attach_pdf: v }))}
            />
            <label htmlFor="attach-pdf-planner" className="text-sm flex items-center gap-2 cursor-pointer">
              <FileText className="w-4 h-4 text-muted-foreground" />
              Attach Dispatch Note PDF
            </label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancel</Button>
          <Button onClick={onSend} disabled={sending || !emailData.to_email || !emailData.subject} data-testid="btn-send-dispatch-email">
            {sending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
            ) : (
              <><Send className="w-4 h-4 mr-2" /> Send Email</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
